import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, Download, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoicePrintPage({ portalMode = false }) {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const token = portalMode
        ? localStorage.getItem('portal_token')
        : localStorage.getItem('crm_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch invoice
      const invUrl = portalMode
        ? `${API_URL}/api/portal/invoice/${invoiceId}`
        : `${API_URL}/api/invoices/${invoiceId}`;
      const invRes = await axios.get(invUrl, { headers });
      setInvoice(invRes.data);

      // Fetch client info
      if (!portalMode && invRes.data.client_id) {
        try {
          const clientRes = await axios.get(`${API_URL}/api/clients/${invRes.data.client_id}`, { headers });
          setClient(clientRes.data);
        } catch (e) { /* client fetch optional */ }
      } else if (portalMode) {
        try {
          const meRes = await axios.get(`${API_URL}/api/portal/me`, { headers });
          setClient(meRes.data);
        } catch (e) { /* client fetch optional */ }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invoice not found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg font-semibold">{error}</p>
          <button onClick={() => window.history.back()} className="mt-4 text-blue-600 hover:underline flex items-center gap-1 mx-auto">
            <ArrowLeft className="w-4 h-4" /> Go Back
          </button>
        </div>
      </div>
    );
  }

  const isPaid = invoice?.status === 'paid';
  const items = invoice?.items || [];
  const subtotal = items.reduce((sum, item) => sum + ((item.quantity || 1) * (item.unit_price || item.rate || 0)), 0);

  const clientName = client?.company_name || client?.first_name
    ? `${client?.first_name || ''} ${client?.last_name || ''}`.trim()
    : invoice?.client_name || '';
  const clientContact = client?.first_name
    ? `${client?.first_name || ''} ${client?.last_name || ''}`.trim()
    : '';

  return (
    <>
      {/* Print/Download bar — hidden when printing */}
      <div className="print:hidden bg-gray-100 border-b px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => window.history.back()} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex gap-2">
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-[#014DB7] text-white text-sm font-medium rounded-lg hover:bg-[#013a8c]" data-testid="print-invoice-btn">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900" data-testid="download-invoice-btn">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="max-w-[800px] mx-auto bg-white p-8 print:p-0 print:max-w-none" id="invoice-print-area">
        {/* PAID Banner */}
        {isPaid && (
          <div className="absolute top-0 right-0 w-[120px] h-[120px] overflow-hidden print:fixed" style={{ position: 'absolute' }}>
            <div className="bg-green-500 text-white font-bold text-lg text-center py-1.5 rotate-45 translate-x-[18px] translate-y-[32px] w-[170px] shadow-md">
              PAID
            </div>
          </div>
        )}

        {/* Header: Logo + Company Info */}
        <div className="flex items-start justify-between mb-8 relative">
          <div>
            <img src="/dme-logo.png" alt="DME R'US" className="h-20 mb-2" />
          </div>
          <div className="text-right text-sm text-gray-700 leading-relaxed">
            <p className="font-bold text-gray-900">Mail Checks to:</p>
            <p>Danny Techera / DME R'US</p>
            <p>36942 State Rd 54</p>
            <p>Suite 101</p>
            <p>Zephyrhills, Florida 33542</p>
            <div className="my-1 border-b border-gray-300 w-48 ml-auto"></div>
            <p className="font-semibold text-gray-600 mt-1">info@dmerus.com</p>
            <p className="text-gray-600">www.dmerus.com</p>
          </div>
        </div>

        {/* Invoice Number + Dates */}
        <div className="bg-gray-100 p-4 border-b-2 border-gray-300 mb-6">
          <h1 className="text-xl font-bold text-gray-900">Invoice #{invoice?.invoice_number}</h1>
          <p className="text-sm text-gray-600 mt-1">Invoice Date: {formatDate(invoice?.created_at)}</p>
          <p className="text-sm text-gray-600">Due Date: {formatDate(invoice?.due_date)}</p>
          {isPaid && invoice?.paid_at && (
            <p className="text-sm text-green-700 font-semibold mt-1">Paid On: {formatDate(invoice.paid_at)}</p>
          )}
        </div>

        {/* Invoiced To */}
        <div className="mb-8">
          <p className="font-bold text-gray-900 mb-1">Invoiced To</p>
          {(client?.company_name || invoice?.client_name) && (
            <p className="text-sm text-gray-800 font-semibold uppercase">{client?.company_name || invoice?.client_name}</p>
          )}
          {clientContact && clientContact !== (client?.company_name || '') && (
            <p className="text-sm text-gray-700">ATTN: {clientContact}</p>
          )}
          {(client?.address || client?.street) && (
            <p className="text-sm text-gray-700">{client?.address || client?.street}</p>
          )}
          {(client?.city || client?.state || client?.zip_code) && (
            <p className="text-sm text-gray-700">
              {[client?.city, client?.state].filter(Boolean).join(', ')}{client?.zip_code ? ' ' + client.zip_code : ''}
            </p>
          )}
          {client?.country && <p className="text-sm text-gray-700">{client.country}</p>}
          {client?.email && (
            <p className="text-sm text-gray-600 mt-2">{client.email}</p>
          )}
          {client?.phone && (
            <p className="text-sm text-gray-600">{client.phone}</p>
          )}
        </div>

        {/* Line Items Table */}
        <table className="w-full border-collapse mb-6">
          <thead>
            <tr className="bg-gray-200">
              <th className="text-left p-3 text-sm font-bold text-gray-700 border border-gray-300">Description</th>
              <th className="text-center p-3 text-sm font-bold text-gray-700 border border-gray-300 w-20">Qty</th>
              <th className="text-right p-3 text-sm font-bold text-gray-700 border border-gray-300 w-28">Rate</th>
              <th className="text-right p-3 text-sm font-bold text-gray-700 border border-gray-300 w-32">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} className="border border-gray-300">
                <td className="p-3 text-sm text-gray-800 border border-gray-300">
                  <p className="font-medium">{item.name}</p>
                  {item.description && <p className="text-gray-500 text-xs mt-0.5">{item.description}</p>}
                </td>
                <td className="p-3 text-sm text-gray-800 text-center border border-gray-300">{item.quantity || 1}</td>
                <td className="p-3 text-sm text-gray-800 text-right border border-gray-300">{formatCurrency(item.unit_price || item.rate || 0)}</td>
                <td className="p-3 text-sm text-gray-900 text-right font-semibold border border-gray-300">
                  {formatCurrency((item.quantity || 1) * (item.unit_price || item.rate || 0))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-100">
              <td colSpan="3" className="p-3 text-sm font-bold text-gray-700 text-right border border-gray-300">Sub Total</td>
              <td className="p-3 text-sm font-bold text-gray-900 text-right border border-gray-300">{formatCurrency(subtotal)}</td>
            </tr>
            {(invoice?.tax_amount || 0) > 0 && (
              <tr className="bg-gray-100">
                <td colSpan="3" className="p-3 text-sm font-bold text-gray-700 text-right border border-gray-300">Tax ({invoice?.tax_rate || 0}%)</td>
                <td className="p-3 text-sm font-bold text-gray-900 text-right border border-gray-300">{formatCurrency(invoice?.tax_amount)}</td>
              </tr>
            )}
            <tr className="bg-gray-200">
              <td colSpan="3" className="p-3 text-sm font-bold text-gray-900 text-right border border-gray-300">Total</td>
              <td className="p-3 text-sm font-bold text-gray-900 text-right border border-gray-300">{formatCurrency(invoice?.total || subtotal)}</td>
            </tr>
            {invoice?.status !== 'paid' && (
              <tr className="bg-amber-50">
                <td colSpan="3" className="p-3 text-sm font-semibold text-amber-800 text-right border border-gray-300">
                  Processing Fee (2.9% + $0.30)
                </td>
                <td className="p-3 text-sm font-semibold text-amber-800 text-right border border-gray-300" data-testid="invoice-processing-fee">
                  *Applied at checkout
                </td>
              </tr>
            )}
            {invoice?.processing_fee > 0 && invoice?.status === 'paid' && (
              <>
                <tr className="bg-amber-50">
                  <td colSpan="3" className="p-3 text-sm font-semibold text-amber-800 text-right border border-gray-300">
                    Processing Fee (2.9% + $0.30)
                  </td>
                  <td className="p-3 text-sm font-semibold text-amber-800 text-right border border-gray-300" data-testid="invoice-processing-fee-amount">
                    {formatCurrency(invoice.processing_fee)}
                  </td>
                </tr>
                <tr className="bg-gray-300">
                  <td colSpan="3" className="p-3 text-sm font-bold text-gray-900 text-right border border-gray-300">Total Charged</td>
                  <td className="p-3 text-sm font-bold text-gray-900 text-right border border-gray-300" data-testid="invoice-total-charged">
                    {formatCurrency(invoice.transaction_amount || ((invoice?.total || 0) + (invoice?.processing_fee || 0)))}
                  </td>
                </tr>
              </>
            )}
          </tfoot>
        </table>

        {/* Transactions Section (if paid) */}
        {isPaid && (
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Transactions</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="text-left p-3 text-sm font-bold text-gray-700 border border-gray-300">Transaction Date</th>
                  <th className="text-center p-3 text-sm font-bold text-gray-700 border border-gray-300">Gateway</th>
                  <th className="text-center p-3 text-sm font-bold text-gray-700 border border-gray-300">Transaction ID</th>
                  <th className="text-right p-3 text-sm font-bold text-gray-700 border border-gray-300">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border border-gray-300">
                  <td className="p-3 text-sm text-gray-800 border border-gray-300">{formatDate(invoice?.paid_at)}</td>
                  <td className="p-3 text-sm text-gray-800 text-center border border-gray-300">Stripe</td>
                  <td className="p-3 text-sm text-gray-800 text-center border border-gray-300">{invoice?.stripe_payment_intent || invoice?.id?.substring(0, 12) || '-'}</td>
                  <td className="p-3 text-sm text-gray-900 text-right font-semibold border border-gray-300">{formatCurrency(invoice?.paid_amount || invoice?.total)}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="bg-gray-100">
                  <td colSpan="3" className="p-3 text-sm font-bold text-gray-700 text-right border border-gray-300">Balance</td>
                  <td className="p-3 text-sm font-bold text-gray-900 text-right border border-gray-300">
                    {formatCurrency(Math.max(0, (invoice?.total || 0) - (invoice?.paid_amount || invoice?.total || 0)))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Notes */}
        {invoice?.notes && (
          <div className="mb-6 p-4 bg-gray-50 rounded border">
            <p className="text-sm font-semibold text-gray-700 mb-1">Notes</p>
            <p className="text-sm text-gray-600">{invoice.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 mt-8 pt-4 border-t">
          PDF Generated on {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          @page { margin: 0.5in; size: letter; }
        }
      `}</style>
    </>
  );
}
